import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import PageHeader from '../../components/PageHeader';
import ActivityCard from '../../components/courses/ActivityCard';
import ActivityDetailModal from '../../components/courses/ActivityDetailModal';
import SectionFilter, { SectionFilterItem } from '../../components/courses/SectionFilter';
import type { IActivityWithCourses, ICourse } from '../../utils/courseSchedule';
import '../../App.css';
import '../ActivitiesPage.css';
import { translateError } from '../../utils/errorMessages';

interface IMyActivity {
  _id: number;
  name: string;
  description: string;
  coverImage?: string;
  joinPolicy: string;
  joinedMemberIds: number[];
  isMember: boolean;
  courses?: ICourse[];
}

interface IUserMember {
  _id: string;
  name: string;
  color?: string;
  groups?: { _id: string; name: string }[];
}

/** Query param that deep-links a single activity's detail modal. */
const ACTIVITY_PARAM = 'activity';

const toActivityWithCourses = (a: IMyActivity): IActivityWithCourses => ({
  _id: a._id,
  name: a.name,
  description: a.description,
  coverImage: a.coverImage,
  joinPolicy: a.joinPolicy,
  courses: a.courses || [],
});

const MyActivitiesPage: React.FC = () => {
  const { t } = useTranslation();
  const [activities, setActivities] = useState<IMyActivity[]>([]);
  const [sections, setSections] = useState<SectionFilterItem[]>([]);
  const [selectedSectionId, setSelectedSectionId] = useState<number | null>(null);
  const [userMembers, setUserMembers] = useState<IUserMember[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedActivity, setSelectedActivity] = useState<IActivityWithCourses | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const activityParam = searchParams.get(ACTIVITY_PARAM);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [activitiesRes, membersRes, sectionsRes] = await Promise.all([
        api.activities.listMy(),
        api.my.members(),
        api.sections.listMy().catch(() => ({ data: [] })),
      ]);
      const members = (membersRes.data as IUserMember[]) || [];
      setActivities(activitiesRes.data as IMyActivity[]);
      setSections(sectionsRes.data as SectionFilterItem[]);
      setUserMembers(members);
      // Always keep a member selected when the user has at least one; default to
      // the first and only reset if the current selection disappeared.
      setSelectedMemberId((prev) => {
        if (members.length === 0) return null;
        if (prev != null && members.some((m) => Number(m._id) === prev)) return prev;
        return Number(members[0]._id);
      });
      setLoading(false);
    } catch (err: any) {
      setError(translateError(err, t));
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // The URL owns which activity is open (?activity=<id>), so the modal survives
  // a reload and browser back closes it. Ids resolve against the fetched list
  // only — that list is scoped to what this user may see, so a link to an
  // activity they have no access to simply falls back to the grid.
  useEffect(() => {
    if (loading || error) return;
    if (!activityParam) {
      setSelectedActivity(null);
      return;
    }
    const found = activities.find((a) => String(a._id) === activityParam);
    if (!found) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(ACTIVITY_PARAM);
          return next;
        },
        { replace: true },
      );
      return;
    }
    // Preserve the object when the id is unchanged: `onChanged` refetches after
    // every join/leave, and handing the modal a fresh object for the same
    // activity would reset its course state and wipe the pay-prompt banner.
    setSelectedActivity((prev) =>
      prev && Number(prev._id) === Number(found._id) ? prev : toActivityWithCourses(found),
    );
  }, [activityParam, activities, loading, error, setSearchParams]);

  const openActivity = useCallback(
    (activity: IActivityWithCourses) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set(ACTIVITY_PARAM, String(activity._id));
        return next;
      });
    },
    [setSearchParams],
  );

  const closeActivity = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete(ACTIVITY_PARAM);
      return next;
    });
  }, [setSearchParams]);

  const visibleActivities = useMemo(() => {
    if (selectedSectionId == null) return activities;
    const section = sections.find((s) => s._id === selectedSectionId);
    if (!section) return activities;
    const allowed = new Set<number>(section.activityIds.map(Number));
    return activities.filter((a) => allowed.has(Number(a._id)));
  }, [activities, sections, selectedSectionId]);

  if (loading && activities.length === 0) {
    return <p>{t('common.loading')}</p>;
  }

  if (error) {
    return <p className="error-message">{t('common.error')}: {error}</p>;
  }

  const renderMemberSwitch = () => {
    if (userMembers.length === 0) {
      return (
        <p className="activities-member-switch__empty">
          <Link to="/dashboard/members">{t('courses.noMembers')}</Link>
        </p>
      );
    }

    if (userMembers.length === 1) return null;

    // Pill switch for a handful of members; a styled select once the list grows.
    if (userMembers.length <= 4) {
      return (
        <div className="activities-member-switch">
          <span className="activities-member-switch__label">{t('activities.viewingAs')}</span>
          <div className="activities-member-switch__pills">
            {userMembers.map((m) => {
              const id = Number(m._id);
              return (
                <button
                  key={m._id}
                  type="button"
                  className={`activities-member-switch__pill${
                    selectedMemberId === id ? ' is-active' : ''
                  }`}
                  onClick={() => setSelectedMemberId(id)}
                >
                  {m.name}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    return (
      <div className="activities-member-switch">
        <label className="activities-member-switch__label" htmlFor="activities-member-select">
          {t('activities.viewingAs')}
        </label>
        <select
          id="activities-member-select"
          className="activities-member-switch__select"
          value={selectedMemberId != null ? String(selectedMemberId) : ''}
          onChange={(e) => setSelectedMemberId(Number(e.target.value))}
        >
          {userMembers.map((m) => (
            <option key={m._id} value={m._id}>
              {m.name}
            </option>
          ))}
        </select>
      </div>
    );
  };

  return (
    <div className="admin-list-page">
      <PageHeader title={t('activities.title')} />

      {renderMemberSwitch()}

      <SectionFilter
        sections={sections}
        activities={activities}
        selectedId={selectedSectionId}
        onChange={setSelectedSectionId}
      />

      {visibleActivities.length === 0 ? (
        <div className="no-activities">
          <p>{t('activities.noPublicActivities')}</p>
        </div>
      ) : (
        <div className="activities-poster-grid">
          {visibleActivities.map((activity) => (
            <ActivityCard
              key={activity._id}
              activity={toActivityWithCourses(activity)}
              onSelect={openActivity}
              highlightMemberId={selectedMemberId}
            />
          ))}
        </div>
      )}

      <ActivityDetailModal
        activity={selectedActivity}
        onClose={closeActivity}
        onChanged={fetchData}
        activeMemberId={selectedMemberId}
        hideMemberSelector
      />
    </div>
  );
};

export default MyActivitiesPage;
